// Umfeld-Check: welche Projekte — und damit welche Koordinatoren — liegen in der
// Naehe einer neuen Baustelle? Urspruenglich nur im ProjectSetupWizard vorhanden
// (calculateDistance + checkNearbyProjects), hier herausgeloest, damit der Flow
// "Angebot angenommen -> Auftrag" dieselbe Anzeige bekommt statt einer Kopie.
//
// Bewusst OHNE Supabase-/Fetch-Import: die Ableitung bleibt rein und damit ohne
// DOM-/Netz-Testumgebung pruefbar. Die unreinen Teile (Geocoding, Projektabruf)
// liegen in src/hooks/useNearbyProjects.ts.

export interface Coordinates {
    lat: number;
    lng: number;
}

/** Ein Projekt im Umkreis, normalisiert fuer die Anzeige. */
export interface NearbyProject {
    id: string;
    name: string;
    projectNumber: string;
    /** Anzeigename des zustaendigen Koordinators, '' wenn nicht hinterlegt. */
    coordinator: string;
    address: string;
    distanceKm: number;
}

/** Suchradius des Umfeld-Checks (wie bisher im Wizard). */
export const NEARBY_RADIUS_KM = 50;
/** Maximale Anzahl angezeigter Nachbarprojekte (wie bisher im Wizard). */
export const NEARBY_LIMIT = 5;

/**
 * Luftlinie zwischen zwei Koordinaten in Kilometern (Haversine).
 * Keine Fahrstrecke — Verkehrswege und Gelaende fliessen nicht ein.
 */
export const calculateDistanceKm = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371; // Erdradius in km
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
};

/**
 * Baut die Geocoding-Abfrage aus Adressteilen. Leere Teile fallen weg, damit
 * keine Fragmente wie ", 21079 Hamburg" an Nominatim gehen. '' bedeutet:
 * nichts zu suchen.
 */
export const buildAddressQuery = (parts: { street?: string; zip?: string; city?: string }): string => {
    const street = (parts.street || '').trim();
    const zip = (parts.zip || '').trim();
    const city = (parts.city || '').trim();
    return [street, [zip, city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
};

/** Adress-Abfragen vergleichbar machen: Gross-/Kleinschreibung und Leerraum egal. */
const normalizeQuery = (query: string | null | undefined): string =>
    (query || '').toLowerCase().replace(/\s+/g, ' ').trim();

/**
 * Was beim naechsten Lookup zu tun ist:
 * - 'clear'   : keine Adresse mehr -> Koordinaten und Umfeld verwerfen
 * - 'skip'    : identisch zur letzten ERFOLGREICHEN Abfrage -> nichts tun
 *               (die Adressfelder feuern onBlur auch ohne Aenderung)
 * - 'geocode' : neue Adresse -> verorten
 */
export type LookupPlan = 'clear' | 'skip' | 'geocode';

export const planNearbyLookup = (
    query: string | null | undefined,
    lastSuccessfulQuery: string | null | undefined,
): LookupPlan => {
    const q = normalizeQuery(query);
    if (!q) return 'clear';
    const last = normalizeQuery(lastSuccessfulQuery);
    return last && q === last ? 'skip' : 'geocode';
};

/**
 * Wie mit bereits vorhandenen Koordinaten umzugehen ist, wenn das Geocoding
 * fehlschlaegt:
 * - 'keep'  : gleiche Adresse wie beim letzten Erfolg -> transienter Fehler
 *             (Timeout, Rate-Limit). Gueltige Koordinaten NICHT wegwerfen,
 *             sonst entstuende ein Projekt ohne Standort.
 * - 'clear' : andere Adresse -> alte Koordinaten waeren veraltet.
 */
export const planGeocodeFailure = (
    query: string | null | undefined,
    lastSuccessfulQuery: string | null | undefined,
): 'keep' | 'clear' => {
    const last = normalizeQuery(lastSuccessfulQuery);
    return last && normalizeQuery(query) === last ? 'keep' : 'clear';
};

/**
 * Ist die Antwort eines Lookups ueberholt? Die Adressfelder starten
 * ueberlappende Abfragen (Strasse -> PLZ -> Ort); ohne diese Pruefung koennte
 * eine spaet eintreffende Antwort zu einer aelteren, unvollstaendigeren Adresse
 * das Ergebnis der neueren ueberschreiben. Nur die juengste Generation schreibt.
 */
export const isStaleGeneration = (generation: number, current: number): boolean =>
    generation !== current;

/**
 * Select des Umfeld-Checks: nur die Spalten und JSON-Pfade, die
 * findNearbyProjects auswertet — NIE die ganze Spalte `data`.
 *
 * Warum: bis 23.10.2026 zog jeder Check `id, name, data, project_number` fuer
 * ALLE Projekte; auf PROD waren das 610 Projekte mit 16,1 MB `data` (Schnitt
 * 27 KB) je Adress-Lookup, obwohl nur Koordinaten, Name, Nummer, Koordinator
 * und Adresse gebraucht werden. Die JSON-Pfade sind reine Lese-Syntax von
 * PostgREST (keine Migration): `->` liefert den JSON-Wert (das Koordinaten-
 * Objekt), `->>` den Text.
 */
export const NEARBY_PROJECT_SELECT = [
    'id',
    'name',
    'project_number',
    'koordinaten:data->metadata->globalData->locationCoordinates',
    'metaName:data->metadata->>projectName',
    'metaNummer:data->metadata->>projectNumber',
    'koordinator:data->metadata->>coordinator',
    'adresse:data->metadata->>projectAddress',
].join(', ');

/**
 * Schlanke Zeile aus public.projects, wie NEARBY_PROJECT_SELECT sie liefert.
 * Die JSON-Felder kommen aus dem Blob und sind nicht garantiert typrein,
 * deshalb wird jedes Feld beim Abbilden einzeln geprueft.
 */
export interface NearbyProjectRow {
    id?: string | null;
    name?: string | null;
    project_number?: string | null;
    /** `data.metadata.globalData.locationCoordinates` — erwartet `{ lat, lng }`. */
    koordinaten?: unknown;
    /** `data.metadata.projectName` — Fallback, wenn die Spalte `name` leer ist. */
    metaName?: string | null;
    /** `data.metadata.projectNumber` — Fallback fuer die Spalte `project_number`. */
    metaNummer?: string | null;
    /** `data.metadata.coordinator` */
    koordinator?: string | null;
    /** `data.metadata.projectAddress` */
    adresse?: string | null;
}

/**
 * Eine Koordinate aus dem `locationCoordinates`-Objekt. Fehlt das Objekt oder
 * ist es kein Objekt, ergibt das NaN — genau wie frueher `Number(coords?.lat)`.
 */
const koordinate = (koordinaten: unknown, schluessel: 'lat' | 'lng'): number =>
    koordinaten !== null && typeof koordinaten === 'object'
        ? Number((koordinaten as Record<string, unknown>)[schluessel])
        : NaN;

export interface FindNearbyOptions {
    radiusKm?: number;
    limit?: number;
    /** Projekt-Id, die nie als "Nachbar" auftauchen soll (z.B. das eigene). */
    excludeProjectId?: string;
}

/**
 * Filtert Projektzeilen auf den Umkreis um `origin`, sortiert nach Entfernung
 * und begrenzt die Trefferzahl. Zeilen ohne hinterlegte Koordinaten fallen
 * heraus — sie liefern keine belastbare Entfernung.
 */
export const findNearbyProjects = (
    rows: NearbyProjectRow[] | null | undefined,
    origin: Coordinates,
    options: FindNearbyOptions = {},
): NearbyProject[] => {
    const radiusKm = options.radiusKm ?? NEARBY_RADIUS_KM;
    const limit = options.limit ?? NEARBY_LIMIT;
    if (!rows || !Number.isFinite(origin?.lat) || !Number.isFinite(origin?.lng)) return [];

    return rows
        .map((row): NearbyProject | null => {
            if (!row || (options.excludeProjectId && row.id === options.excludeProjectId)) return null;
            const lat = koordinate(row.koordinaten, 'lat');
            const lng = koordinate(row.koordinaten, 'lng');
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
            return {
                id: String(row.id ?? ''),
                name: String(row.name || row.metaName || 'Unbenanntes Projekt'),
                projectNumber: String(row.project_number || row.metaNummer || ''),
                coordinator: String(row.koordinator || ''),
                address: String(row.adresse || ''),
                distanceKm: calculateDistanceKm(origin.lat, origin.lng, lat, lng),
            };
        })
        .filter((p): p is NearbyProject => p !== null && p.distanceKm < radiusKm)
        .sort((a, b) => a.distanceKm - b.distanceKm)
        .slice(0, limit);
};

/** Namen vergleichbar machen: Gross-/Kleinschreibung und Mehrfach-Leerzeichen egal. */
export const normalizeCoordinatorName = (name: string): string =>
    (name || '').toLowerCase().replace(/\s+/g, ' ').trim();

/**
 * Kuerzeste Entfernung, in der dieser Koordinator bereits ein Projekt betreut —
 * die Antwort auf "wer ist in der Naehe?". null, wenn kein Nachbarprojekt auf
 * den Namen laeuft (Namensschreibweisen weichen ab oder es gibt schlicht keins).
 */
export const coordinatorDistanceKm = (projects: NearbyProject[], fullName: string): number | null => {
    const key = normalizeCoordinatorName(fullName);
    if (!key) return null;
    const hits = (projects || [])
        .filter((p) => normalizeCoordinatorName(p.coordinator) === key)
        .map((p) => p.distanceKm);
    return hits.length > 0 ? Math.min(...hits) : null;
};

/** Entfernung deutsch formatiert ("12,3 km"). */
export const formatDistanceKm = (km: number): string =>
    `${km.toFixed(1).replace('.', ',')} km`;

export interface CoordinatorLike {
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
}

export interface CoordinatorOption {
    id: string;
    /** Anzeigename (Vorname Nachname, sonst E-Mail). */
    label: string;
    /** Entfernung des naechsten eigenen Projekts, null wenn keins im Umkreis. */
    distanceKm: number | null;
}

/**
 * Koordinatorenliste fuer die Auswahl: wer bereits ein Projekt im Umkreis
 * betreut, steht oben (naechster zuerst), alle anderen alphabetisch danach.
 * Rein, damit die Reihenfolge ohne DOM testbar bleibt.
 */
export const buildCoordinatorOptions = (
    users: CoordinatorLike[] | null | undefined,
    projects: NearbyProject[],
): CoordinatorOption[] => {
    const options = (users || []).map((u) => {
        const name = `${u.first_name || ''} ${u.last_name || ''}`.replace(/\s+/g, ' ').trim();
        const label = name || (u.email || '').trim() || u.id;
        return { id: u.id, label, distanceKm: coordinatorDistanceKm(projects, name) };
    });
    return options.sort((a, b) => {
        if (a.distanceKm !== null && b.distanceKm !== null) return a.distanceKm - b.distanceKm;
        if (a.distanceKm !== null) return -1;
        if (b.distanceKm !== null) return 1;
        return a.label.localeCompare(b.label, 'de');
    });
};
